import { Component, OnDestroy, OnInit, Injector, Input, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FieldConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { AbstractField } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/abstract-field';
import { getObservableFromAsyncValueSafe } from '@ordersnproducts/modules/shared/models/async-value';
import { OneClickJobsiteSalesAreaState } from '@ordersnproducts/modules/one-click-order/reducers/one-click-jobsite-sales-area.reducer';
import { State } from '@ordersnproducts/modules/one-click-order/reducers';
import { Store, createSelector } from '@ngrx/store';
import { getOneClickJobsiteSalesAreaState } from '@ordersnproducts/modules/one-click-order/selectors/one-click-jobsite-sales-area.selector';
import { distinctUntilChangedDeep } from '@ordersnproducts/modules/shared/utils/distinct-until-changed-deep';
import { Subscription } from 'rxjs';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OneClickPreviewField } from '@ordersnproducts/modules/one-click-order/util/constants/preview-fields';
import { NgrxFormControl } from '@ordersnproducts/modules/shared/models/ngrx-form-control/ngrx-form-control';
import { getOneClickSelectedOrder } from '@ordersnproducts/modules/one-click-order/selectors/one-click-orders.selector';
import { TranslateService } from '@ngx-translate/core';
import { getOneValidatedPurchaseOrder } from '@ordersnproducts/modules/one-click-order/selectors/one-click-purchase-order-selector';

const purchaseOrderData = createSelector(
  getOneClickJobsiteSalesAreaState,
  getOneClickSelectedOrder,
  getOneValidatedPurchaseOrder,
  (purchaseOrderRequired, selectedOrder, purchaseOrderValidated) => {
    return { purchaseOrderRequired, selectedOrder, purchaseOrderValidated };
  },
);

@Component({
  selector: 'app-purchase-order',
  templateUrl: './purchase-order.component.html',
  styleUrls: ['./purchase-order.component.scss'],
})
export class PurchaseOrderComponent extends AbstractField implements OnInit, OnDestroy {
  @Input()
  form: ConfigurableFormComponent;

  public config: FieldConfig;
  public group: UntypedFormGroup;
  public fieldControl: UntypedFormControl;
  public placeholder: string;
  public required = false;
  public errorMessage: string;
  public oneClickPreviewField = OneClickPreviewField;

  private ngDestroy = new Subject();

  constructor(
    private store: Store<State>,
    private injector: Injector,
    private changeDetector: ChangeDetectorRef,
  ) {
    super();
  }

  get control(): NgrxFormControl {
    return this.group.controls[OneClickPreviewField.PurchaseOrder] as NgrxFormControl;
  }

  ngOnInit(): void {
    this.subscribeState();

    if (this.config.placeholder) {
      getObservableFromAsyncValueSafe(this.config.placeholder)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((text) => {
          this.placeholder = text;
        });
    }
  }

  public subscribeState(): Subscription {
    return this.store
      .select(purchaseOrderData)
      .pipe(takeUntil(this.ngDestroy), distinctUntilChangedDeep())
      .subscribe(({ purchaseOrderRequired, selectedOrder, purchaseOrderValidated }) => {
        if ((purchaseOrderRequired && selectedOrder) || purchaseOrderValidated) {
          this.setMandatory(purchaseOrderRequired);
          this.updateErrorMessage();
          this.changeDetector.markForCheck();
          this.changeDetector.detectChanges();
        }
      });
  }

  public setMandatory(oneClickJobsiteSalesAreaState: OneClickJobsiteSalesAreaState): void {
    this.required = oneClickJobsiteSalesAreaState.purchaseOrderRequired;
    const validators: ValidatorFn[] = [];
    validators.push(...this.config.validation);
    if (oneClickJobsiteSalesAreaState.purchaseOrderRequired) {
      validators.push(Validators.required);
    } else {
      this.control.clearValidators();
    }
    this.control.setValidators(validators);
  }

  public updateErrorMessage() {
    const translate: TranslateService = this.injector.get(TranslateService);
    if (this.control.hasError('required')) {
      this.errorMessage = 'forms.required';
    } else if (this.control.hasError('purchaseOrderInvalid')) {
      this.errorMessage = 'views.location.purchase_order_invalid';
    } else if (this.control.hasError('maxlength')) {
      this.errorMessage = `${translate.instant('forms.maxlength')}: ${
        this.control.errors['maxlength']['requiredLength']
      }`;
    }
    this.control.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }
}
